import * as React from 'react';

import { useApolloClient } from '@apollo/client';

import type { CohortAudience, Learner } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { LEARNER_LIST_PAGE_SIZE } from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';
import EnterpriseLearnerCohortFetchDoc from 'bundles/enterprise-admin-messaging-v2/queries/EnterpriseLearnerCohortFetch.graphql';
import type {
  EnterpriseLearnerCohortFetchQuery,
  EnterpriseLearnerCohortFetchQueryVariables,
} from 'bundles/enterprise-admin-messaging-v2/queries/__generated__/EnterpriseLearnerCohortFetch';

type Params = {
  cohortAudience?: CohortAudience;
  /** 1-indexed page number. */
  page: number;
  /** When true, the hook is skipped (e.g. when the admin is in search mode). */
  skip?: boolean;
};

type Result = {
  learners: Learner[];
  total: number;
  loading: boolean;
  error: boolean;
};

/**
 * Paginates the cohort members directly via `fetchEnterpriseLearnerCohorts`.
 * Used as the default view in the edit-learner screen (before the admin
 * types a search query). Returns learners in the cohort's natural order.
 */
export const useCohortLearnerPaging = ({ cohortAudience, page, skip }: Params): Result => {
  const client = useApolloClient();
  const [learners, setLearners] = React.useState<Learner[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);

  const shouldFetch = Boolean(cohortAudience) && !skip;
  const pageToken = (page - 1) * LEARNER_LIST_PAGE_SIZE;

  React.useEffect(() => {
    if (!cohortAudience || skip) {
      setLearners([]);
      setTotal(0);
      setLoading(false);
      setError(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    client
      .query<EnterpriseLearnerCohortFetchQuery, EnterpriseLearnerCohortFetchQueryVariables>({
        query: EnterpriseLearnerCohortFetchDoc,
        variables: {
          cohortId: cohortAudience.cohortId,
          orgId: cohortAudience.orgId,
          pageSize: LEARNER_LIST_PAGE_SIZE,
          pageToken,
        },
        context: { clientName: 'gatewayGql' },
        fetchPolicy: 'no-cache',
      })
      .then((res) => {
        if (cancelled) return;
        const result = res.data?.EnterpriseLearnerCohort?.fetchEnterpriseLearnerCohorts;
        const fetched: Learner[] = (result?.learners ?? []).map((l) => ({
          id: l.email ?? '',
          name: l.fullName ?? '',
          email: l.email ?? '',
        }));
        setLearners(fetched);
        setTotal(result?.cohortSize ?? 0);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, cohortAudience?.cohortId, cohortAudience?.orgId, pageToken, shouldFetch]);

  return { learners, total, loading, error };
};

export default useCohortLearnerPaging;
